import { BCMSCurrency, useBCMSContext, useStateManager, useUIState } from "@bcm-dev/bcms-vite";
import { Account, ContactInfo, NotificationDetail } from "model";

import UINotifiButton from "components/shared/notifi/UINotifiButton";
import UINotifiToggle from "components/shared/notifi/UINotifiToggle";
import { UIAmountInput, UICheckbox } from "components/ui";
import { SVGAsset } from "res";
import ScheduleAlertWidget from "../ScheduleAlertWidget";

import "./styles.css";
import "./mobile.css";


export default function AlertDetailsAccountWidget (props) {
    const alertDetail = props.alertDetail ?? NotificationDetail.firstInstanceOf();
    const alertID = alertDetail.id;

    const { state } = useBCMSContext();
    const { setTimeOfDayIsValid, updateContactIds, updateAmount, onAddDelete, onUpdate } = useStateManager();
    const { key, config } = useUIState(`Detail_${ alertID }`, getAlertDetailsAccountWidgetConfig());
    
    const hasAccount = alertDetail.account instanceof Account;
    const account = alertDetail.account;

    const nickname = hasAccount ? account.nickname : alertDetail.notification.name;
    const number = hasAccount ? account.number : "";
    const type = hasAccount ? account.type : "";
    const icon = type === "checking" ? "CheckPenIcon" : "PiggyBankIcon";

    const showAddBtn = alertDetail.isEnabled === false;
    const hasUpdate = JSON.stringify(alertDetail.record) !== JSON.stringify(NotificationDetail.initByID(alertID).record);

    const hasScheduledAlert = alertDetail.scheduledAlert ? true : false;

    const hasTimeAlertEnabled = hasScheduledAlert ? setTimeOfDayIsValid(alertDetail) : false;
    //const hasTimeAlertEnabled = hasScheduledAlert ? alertDetail.timeAlert.isEnabled : false;
    let disableAdd = "";
    let disableUpdate = !hasUpdate ? "disabled" : "";
    const toggle = !alertDetail.isEnabled ? config.toggle.isActive : alertDetail.isEnabled;

    // Compare state scheduled alert with global scheduled alert since json stringify loses reference to the "TimeAlert"
    // class instance that exists for a notification detail (instead of "TimeAlert", you will see id "0" for example).
    if(hasScheduledAlert) {
        // "Add" button is disabled for sheduled alerts until a valid schedule alert has been set.
        disableAdd = hasTimeAlertEnabled ? "" : "disabled";
        let hasTimeAlertUpdate = JSON.stringify(alertDetail.scheduledAlert.record) !== JSON.stringify(NotificationDetail.initByID(alertID).scheduledAlert.record);
        
        if(JSON.stringify(alertDetail.scheduledAlert.scheduledTime.record) !== JSON.stringify(NotificationDetail.initByID(alertID).scheduledAlert.scheduledTime.record)) {
            hasTimeAlertUpdate = true;
        }

        if(hasTimeAlertUpdate) disableUpdate = false;
    }

    const amount = BCMSCurrency.toDecimal(alertDetail.amount.amount) === "NaN"
        ? undefined
        : BCMSCurrency.toDecimal(alertDetail.amount.amount);

    const contactSelections = ContactInfo.initAll()
        .sort((a, b) => {
            if(a.type === "alert") { return -1; }
            else if(a.type !== "alert") { return 1; }
            else { return 0; }
        })
        .reduce((contacts, info) => {
            const isSelected = alertDetail.contactInfoEnabledIDs.includes(info.id);

            if(state.branding.options.notifi_version_abi22 === "Notifi" || info.type !== "phone") {
                contacts.push(
                    <AlertDetailsContactSelect
                        key={ `Contact_${ info.id }_${ alertID }`}
                        contact={ info }
                        dataPoint={ alertDetail.account ? alertDetail.account.id : 0 }
                        isSelected={ isSelected }
                        onChange={ updateContactIds(alertID) } />
                );
            } 

            return contacts;
        }, []);

    const onAdd = evt => onAddDelete(alertID, key, "added", true);
    const onDelete = value => !value && alertDetail.isEnabled ? onAddDelete(alertID, key, "deleted", false) : undefined;

    return (
        <div className="alert-details-account-widget">
            <div className="flex-container flex-vertical">
            <div className="flex-container flex-horizontal">
                <div className="set-width row">
                    <UINotifiToggle value={ toggle } onChange={ onDelete } dataPoint={`notifi_toggle_${alertID}`}/>
                </div>
                <div className="flex-container flex-width information-group">
                    <div className="flex-container flex-width row">
                        { hasAccount && <SVGAsset id={ icon } className="set-width account-icon" /> }
        
                        <div className="flex-container flex-vertical account row">
                            <p>{ nickname }</p>
                            <p>{ number }</p>
                        </div>
                    </div>
                    
                    { hasAccount && !hasScheduledAlert &&
                        <div className="flex-width row">
                            <UIAmountInput value={ amount } onBlur={ updateAmount(alertID) } dataPoint={`notifi_amount_input_${alertID}`}/>
                        </div> 
                    }
        
                    { hasScheduledAlert &&
                    <div className="flex-width row">
                        <ScheduleAlertWidget alertDetail={ alertDetail } scheduledAlert={ alertDetail.scheduledAlert } /> 
                    </div>      
                    }
                    <div className="flex-container flex-vertical flex-width row">
                        { contactSelections }
                    </div>
                    <div className="set-width row text-right button-column">
                        {/* The "Update" buttons is to be displayed when the account is toggled on */}
                        { !showAddBtn && <UINotifiButton text="Update" className={ `clickable ${ disableUpdate }` } onClick={ onUpdate(alertID, key) } dataPoint={`notifi_alert_button_${alertID}`}/> }
                        { showAddBtn && <UINotifiButton text="Add" className={ `clickable ${ disableAdd }` } onClick={ onAdd } dataPoint={`notifi_alert_button_${alertID}`}/> }
                    </div>
                </div>
            </div>
            {/* This can be hidden using the hide class */}
            { <p className={`successful ${config.action.length > 0 ?"":"hide"}`}>Subscription <span data-point={"alert_message_"+alertID}>{config.action}</span> successfully.</p> }
            </div>
        </div>
    );
}

export const getAlertDetailsAccountWidgetConfig = () => ({ action: "", toggle: { isActive: false }});

function AlertDetailsContactSelect(props) {
    const contact = props.contact ?? ContactInfo.firstInstanceOf();
    const isSelected = props.isSelected ?? false;
    const dataPoint = props["data-point"] ? props["data-point"] : props.dataPoint ?? "";
    const icon = ContactInfo.getContactInfoIcon(contact);

    const onChange = evt => props.onChange instanceof Function
        ? props.onChange(contact.id)
        : undefined;

    return (
        <div className="alert-details-contact-select">
            <div className="flex-container align-center">
                <div className="contact-icon-container">
                    <SVGAsset id={ icon } />
                </div>

                <UICheckbox value={ isSelected } label={ contact.value } onChange={ onChange } dataPoint={`${dataPoint}_contactInfo_${contact.id}`}/>
            </div>
        </div>
    );
}